import { useEffect, useRef, useState } from "react";
import { useAuth } from "./useAuth";
import { useUI } from "../providers/MoleUIContext";

/** Record types that carry a backend data_access_scope. */
export type DataScopeResource =
	| "sale"
	| "payment"
	| "pending_deposit"
	| "deposit"
	| "expense"
	| "wallet_record"
	| "voucher"
	| "offer"
	| "supply"
	| "supply_payment"
	| "movement"
	| "discard"
	| "conversion"
	| "production"
	| "inventory";

export type DataScopeKind = "ALL" | "OWN" | "MAPPED" | "NONE";

export type ResolvedDataScope = {
	scope: DataScopeKind;
	/** ANY, TODAY, CURRENT_WEEK, CURRENT_MONTH, CURRENT_YEAR or LAST_DAYS. */
	period: string;
	/** N for LAST_DAYS, otherwise null. */
	days?: number | null;
	/** First visible day (YYYY-MM-DD), or null when unlimited. */
	from: string | null;
};

export type DataScopes = Partial<Record<DataScopeResource, ResolvedDataScope>>;

// One request per access-grant version, shared by every mounted filter.
let cachedData: DataScopes | null = null;
let cachedTimestamp = 0;
let cachedAccessVersion = -1;
let inFlightPromise: Promise<DataScopes | null> | null = null;
const subscribers = new Set<(scopes: DataScopes | null) => void>();

const CACHE_TTL_MS = 10_000;

export const invalidateDataScopes = (api?: any) => {
	cachedData = null;
	cachedTimestamp = 0;
	inFlightPromise = null;
	if (api && subscribers.size > 0) {
		void fetchScopes(api, undefined, true);
	}
};

const notifySubscribers = (data: DataScopes | null) => {
	for (const sub of subscribers) {
		try {
			sub(data);
		} catch {
			/* ignore */
		}
	}
};

const fetchScopes = async (
	api: any,
	queueReRun?: (fn: () => void) => void,
	force = false,
): Promise<DataScopes | null> => {
	const now = Date.now();
	if (!force && cachedData && now - cachedTimestamp < CACHE_TTL_MS) {
		return cachedData;
	}
	if (inFlightPromise) {
		return inFlightPromise;
	}
	inFlightPromise = (async () => {
		try {
			const res = await api.get("/users/me/data-scopes");
			if (res?.status === 200) {
				const data = res.data as DataScopes;
				cachedData = data;
				cachedTimestamp = Date.now();
				notifySubscribers(data);
				return data;
			}
			if (res?.status === 401 || res?.status === 422) {
				invalidateDataScopes();
				if (queueReRun) queueReRun(() => void fetchScopes(api, queueReRun, true));
			}
			return cachedData;
		} catch {
			return cachedData;
		} finally {
			inFlightPromise = null;
		}
	})();
	return inFlightPromise;
};

/**
 * Resolved read scopes from GET /users/me/data-scopes. The backend already
 * narrows every list; this only lets the UI bound date pickers and explain
 * why a list is narrowed. Null until loaded (treat as unrestricted).
 */
export const useDataScopes = (): DataScopes | null => {
	const { api, accessVersion } = useAuth();
	const { queueReRun } = useUI();
	const [scopes, setScopes] = useState<DataScopes | null>(() => cachedData);
	const apiRef = useRef(api);
	const queueReRunRef = useRef(queueReRun);
	apiRef.current = api;
	queueReRunRef.current = queueReRun;

	useEffect(() => {
		let cancelled = false;
		const handleUpdate = (updated: DataScopes | null) => {
			if (!cancelled) setScopes(updated);
		};
		subscribers.add(handleUpdate);

		const shouldForce = cachedAccessVersion !== accessVersion;
		if (shouldForce) {
			cachedAccessVersion = accessVersion;
			invalidateDataScopes();
		}

		void fetchScopes(apiRef.current, queueReRunRef.current, shouldForce).then((data) => {
			if (!cancelled && data) setScopes(data);
		});

		const onFocus = () => {
			if (typeof document !== "undefined" && document.visibilityState === "visible") {
				void fetchScopes(apiRef.current, queueReRunRef.current, true);
			}
		};

		if (typeof window !== "undefined") {
			window.addEventListener("focus", onFocus);
		}
		if (typeof document !== "undefined") {
			document.addEventListener("visibilitychange", onFocus);
		}

		const interval = setInterval(() => {
			if (typeof document !== "undefined" && document.visibilityState === "visible") {
				void fetchScopes(apiRef.current, queueReRunRef.current, true);
			}
		}, 30_000);

		return () => {
			cancelled = true;
			subscribers.delete(handleUpdate);
			if (typeof window !== "undefined") {
				window.removeEventListener("focus", onFocus);
			}
			if (typeof document !== "undefined") {
				document.removeEventListener("visibilitychange", onFocus);
			}
			clearInterval(interval);
		};
	}, [accessVersion]);

	return scopes;
};

const parseDay = (value: string): Date => {
	const [y, m, d] = value.split("-").map(Number);
	return new Date(y, m - 1, d);
};

export type DataScopeSummary = {
	/** Earliest visible day across the resources, or null when any is unlimited. */
	minDate: Date | null;
	/** Narrowest record scope across the resources. */
	scope: DataScopeKind;
	period: string;
	/** N when the bounding period is LAST_DAYS. */
	days: number | null;
	isRestricted: boolean;
	/**
	 * Set when the view lists several record types whose scopes differ (e.g.
	 * movements limited to 7 days, discards unlimited): each gets its own
	 * caption instead of one merged summary.
	 */
	parts: { resource: DataScopeResource; scope: DataScopeKind; period: string; days: number | null; bounded: boolean }[] | null;
};

/**
 * Combine the scopes of the resources a view shows. A list mixing several
 * (e.g. movements and discards) is only bounded where all of them are.
 */
export const summarizeDataScopes = (
	scopes: DataScopes | null,
	resources: DataScopeResource | DataScopeResource[] | undefined,
): DataScopeSummary | null => {
	if (!scopes || !resources) return null;
	const named = (Array.isArray(resources) ? resources : [resources])
		.map((resource) => ({ resource, value: scopes[resource] }))
		.filter((entry): entry is { resource: DataScopeResource; value: ResolvedDataScope } => !!entry.value);
	const list = named.map((entry) => entry.value);
	if (list.length === 0) return null;

	const froms = list.map((item) => item.from);
	const minDate = froms.some((value) => value === null)
		? null
		: froms.map((value) => parseDay(value as string)).reduce((a, b) => (a < b ? a : b));
	const rank: Record<DataScopeKind, number> = { NONE: -1, OWN: 0, MAPPED: 1, ALL: 2 };
	const scope = list.map((item) => item.scope).reduce((a, b) => (rank[a] <= rank[b] ? a : b));
	const bounding = minDate
		? list.find((item) => item.from && parseDay(item.from).getTime() === minDate.getTime())
		: undefined;
	const period = bounding?.period ?? "ANY";
	const days = period === "LAST_DAYS" ? bounding?.days ?? null : null;

	const signature = (item: ResolvedDataScope) => `${item.scope}|${item.period}|${item.days ?? ""}`;
	const differs = named.length > 1 && new Set(list.map(signature)).size > 1;
	const parts = differs
		? named.map(({ resource, value }) => ({
			resource,
			scope: value.scope,
			period: value.period,
			days: value.period === "LAST_DAYS" ? value.days ?? null : null,
			bounded: value.from !== null,
		}))
		: null;

	return { minDate, scope, period, days, isRestricted: scope !== "ALL" || minDate !== null || !!parts, parts };
};
